import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { productTemplateListsApi } from 'api/productTemplateListsApi'
import { usersApi } from 'api/usersApi'
import { PTListsByPTResponse } from 'shared/types/ProductTemplateLists'

type FavoritesState = {
  favoritedSizesForAllPTs: {
    listItems: PTListsByPTResponse[]
    itemsPerPage: number
    paginationToken?: string
  }[]
  currentPaginationToken: string | undefined
  nextPageQueue: string[]
  favoriteToBeRemoved: {
    productTemplateId: string
    sizes: number[]
    productTemplateSlug: string
  } | null
  loggedInToViewFavorites: boolean
  isDeleteSizeModalOpen: boolean
  isSaveFavoritesModalOpen: boolean
  renderSlideOut: boolean
  renderPortal: boolean
  refetchOnCurrencyChange: boolean
}

const initialState: FavoritesState = {
  favoritedSizesForAllPTs: [],
  currentPaginationToken: 'start',
  nextPageQueue: ['start'],
  favoriteToBeRemoved: null,
  loggedInToViewFavorites: false,
  isDeleteSizeModalOpen: false,
  isSaveFavoritesModalOpen: false,
  renderSlideOut: true,
  renderPortal: false,
  refetchOnCurrencyChange: false,
}

const favoritesSlice = createSlice({
  name: 'favorites',
  initialState,
  reducers: {
    setCurrentPaginationToken: (state) => {
      state.currentPaginationToken = state.nextPageQueue[0]
      state.nextPageQueue = state.nextPageQueue.slice(1)
    },
    setFavoriteToBeRemoved: (
      state,
      action: PayloadAction<{
        productTemplateId: string
        sizes: number[]
        productTemplateSlug: string
      }>,
    ) => {
      state.favoriteToBeRemoved = action.payload
      state.isDeleteSizeModalOpen = true
    },
    setLoggedInToViewFavorites: (state, action: PayloadAction<boolean>) => {
      state.loggedInToViewFavorites = action.payload
    },
    setIsDeleteSizeModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isDeleteSizeModalOpen = action.payload
    },
    setIsSaveFavoritesModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isSaveFavoritesModalOpen = action.payload
    },
    setRenderSlideOut: (state, action: PayloadAction<boolean>) => {
      state.renderSlideOut = action.payload
    },
    setRenderPortal: (state, action: PayloadAction<boolean>) => {
      state.renderPortal = action.payload
    },
    setRefetchOnCurrencyChange: (state, action: PayloadAction<boolean>) => {
      state.refetchOnCurrencyChange = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      productTemplateListsApi.endpoints.fetchAllUserFavorites.matchFulfilled,
      (state, action) => {
        // Remove the last pagination token from the queue since it has been used already
        state.nextPageQueue = state.nextPageQueue.slice(1)

        // Check if the newest page has a pagination token for the next page
        // Make sure there are no duplicated tokens in queue
        if (
          action?.payload.paginationToken &&
          !state.nextPageQueue.includes(action.payload.paginationToken)
        ) {
          state.nextPageQueue = [...state.nextPageQueue, action.payload.paginationToken]
        }

        // At start of page 1 return an array with first response
        // If user removes all favorites, return empty array to display empty state message
        if (state.currentPaginationToken === 'start') {
          const payload = Object.keys(action.payload)?.length > 0 ? [action.payload] : []
          state.favoritedSizesForAllPTs = payload
        } else {
          // merge into one array for infinite scroll
          state.favoritedSizesForAllPTs = [...state.favoritedSizesForAllPTs, action.payload]
        }
      },
    )
    builder.addMatcher(
      productTemplateListsApi.endpoints.saveUserFavorites.matchFulfilled,
      (state) => {
        // reset state to initial state to refetch new sorted items
        state.currentPaginationToken = 'start'
        state.favoritedSizesForAllPTs = []
        state.nextPageQueue = ['start']
      },
    )
    builder.addMatcher(productTemplateListsApi.endpoints.deleteFavorite.matchFulfilled, (state) => {
      // reset state to initial state to refetch new sorted items
      state.currentPaginationToken = 'start'
      state.favoritedSizesForAllPTs = []
      state.nextPageQueue = ['start']
    })

    builder.addMatcher(usersApi.endpoints.login.matchFulfilled, (state) => {
      state.loggedInToViewFavorites = false
    })
  },
})

const { actions, reducer: favoritesReducer } = favoritesSlice

export const {
  setCurrentPaginationToken,
  setFavoriteToBeRemoved,
  setLoggedInToViewFavorites,
  setIsDeleteSizeModalOpen,
  setIsSaveFavoritesModalOpen,
  setRenderSlideOut,
  setRenderPortal,
  setRefetchOnCurrencyChange,
} = actions
export { favoritesReducer }
